import { createContext, useContext, useReducer, useCallback, useEffect, useState, ReactNode } from 'react'
import { ProjectFormData, ValidationErrors } from '../types'

const STORAGE_KEY = 'arc_project_draft'

type FormAction =
  | { type: 'SET_FIELD'; field: string; value: any }
  | { type: 'SET_ERRORS'; errors: ValidationErrors }
  | { type: 'CLEAR_ERROR'; field: string }
  | { type: 'RESET_FORM' }
  | { type: 'LOAD_DATA'; data: Partial<ProjectFormData> }

const initialState: ProjectFormData = {
  description: '',
  topics: [],
  gradeLevel: null,
  planning: {
    weeks: 4,
    daysPerWeek: 1,
    lessonDurationMinutes: 45,
    startDate: new Date()
  },
  // Campos de turma/período
  schoolTermId: null,
  groupSchoolTermId: null,
  // Structure Step fields
  projectName: '',
  drivingQuestion: '',
  summary: '',
  learningGoals: [],
  deliverable: '',
  coverImage: null,
  coverImageUrl: null,
  // Standards and Skills Step fields
  skillsAndStandards: []
}

function formReducer(state: ProjectFormData, action: FormAction): ProjectFormData {
  switch (action.type) {
    case 'SET_FIELD': {
      if (action.field.includes('.')) {
        const [parent, child] = action.field.split('.')
        return {
          ...state,
          [parent]: {
            ...(state[parent as keyof ProjectFormData] as any),
            [child]: action.value
          }
        }
      }
      return { ...state, [action.field]: action.value }
    }
    case 'LOAD_DATA':
      return { ...state, ...action.data }
    case 'RESET_FORM':
      return initialState
    default:
      return state
  }
}

interface ProjectFormContextType {
  formData: ProjectFormData
  errors: ValidationErrors
  updateField: (field: string, value: any) => void
  validate: () => boolean
  validateAboutStep: () => boolean
  validateStructureStep: () => boolean
  clearErrors: () => void
  resetForm: () => void
  loadData: (data: Partial<ProjectFormData>) => void
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>
}

const ProjectFormContext = createContext<ProjectFormContextType | undefined>(undefined)

interface ProjectFormProviderProps {
  children: ReactNode
}

export const ProjectFormProvider = ({ children }: ProjectFormProviderProps) => {
  const [formData, dispatch] = useReducer(formReducer, initialState)
  const [errors, setErrors] = useState<ValidationErrors>({})

  const updateField = useCallback((field: string, value: any) => {
    dispatch({ type: 'SET_FIELD', field, value })
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }, [errors])

  // Validação do AboutStep (Step 0)
  const validateAboutStep = useCallback((): boolean => {
    const newErrors: ValidationErrors = {}

    if (!formData.description.trim()) {
      newErrors.description = true
    }

    if (formData.topics.length === 0) {
      newErrors.topics = true
    }

    if (!formData.gradeLevel) {
      newErrors.gradeLevel = true
    }

    setErrors(prev => ({ ...prev, ...newErrors }))
    return Object.keys(newErrors).length === 0
  }, [formData.description, formData.topics, formData.gradeLevel])

  // Validação do StructureStep (Step 1)
  const validateStructureStep = useCallback((): boolean => {
    const newErrors: ValidationErrors = {}

    if (!formData.projectName.trim()) {
      newErrors.projectName = true
    }

    if (!formData.drivingQuestion.trim()) {
      newErrors.drivingQuestion = true
    }

    if (formData.learningGoals.length === 0) {
      newErrors.learningGoals = true
    }

    if (!formData.summary.trim()) {
      newErrors.summary = true
    }

    const deliverableDoc = new DOMParser().parseFromString(formData.deliverable, 'text/html')
    const deliverableText = (deliverableDoc.body.textContent || '').replace(/\u00a0/g, ' ').trim()
    if (!deliverableText) {
      newErrors.deliverable = true
    }

    setErrors(prev => ({ ...prev, ...newErrors }))
    return Object.keys(newErrors).length === 0
  }, [formData.projectName, formData.drivingQuestion, formData.learningGoals, formData.deliverable, formData.summary])

  // Validação completa (todos os steps)
  const validate = useCallback((): boolean => {
    const aboutValid = validateAboutStep()
    const structureValid = validateStructureStep()
    return aboutValid && structureValid
  }, [validateAboutStep, validateStructureStep])

  const clearErrors = useCallback(() => {
    setErrors({})
  }, [])

  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    setErrors({})
  }, [])

  const loadData = useCallback((data: Partial<ProjectFormData>) => {
    dispatch({ type: 'LOAD_DATA', data })
  }, [])

  // Salvar draft no localStorage (debounced) - apenas durante navegação entre steps
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData))
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [formData])

  // Limpar localStorage ao desmontar o componente (sair do fluxo)
  useEffect(() => {
    return () => {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  return (
    <ProjectFormContext.Provider
      value={{
        formData,
        errors,
        updateField,
        validate,
        validateAboutStep,
        validateStructureStep,
        clearErrors,
        resetForm,
        loadData,
        setErrors
      }}
    >
      {children}
    </ProjectFormContext.Provider>
  )
}

export const useProjectForm = (): ProjectFormContextType => {
  const context = useContext(ProjectFormContext)
  if (!context) {
    throw new Error('useProjectForm must be used within a ProjectFormProvider')
  }
  return context
}
